/**
 * @Author: Anandp
 * @Date:   2024-04-17 18:40:50
 * @Last Modified by:   Anandp
 * @Last Modified time: 2025-03-07 10:09:28
 */
import React, { useEffect, useState } from "react";
import { DataGrid } from '@mui/x-data-grid';
import { Box, Paper, IconButton, TextField } from '@mui/material';
import { Edit, Save, Search } from '@mui/icons-material';
import { toast } from 'react-toastify';
import UserapiService from '../../services/userApi';

function UserPage() {
  const accessToken = sessionStorage.getItem("accessToken");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [totalCount, setTotalCount] = useState(0);
  const [editingRows, setEditingRows] = useState({});
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line
  }, [paginationModel, searchText]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await UserapiService.userPagination(
        accessToken,
        paginationModel.page + 1,
        paginationModel.pageSize,
        searchText
      );
      if (response && response.data) {
        const formattedUsers = response.data.map((user, index) => ({
          id: user.id,
          slNo: (paginationModel.page * paginationModel.pageSize) + index + 1,
          firstName: user.firstName || '',
          lastName: user.lastName || '',
          email: user.email || '',
          uniqueOperationId: user.object_id || ''
        }));
        setUsers(formattedUsers);
        setTotalCount(response.totalCount || response.data.length);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (id) => {
    setEditingRows(prev => ({ ...prev, [id]: true }));
  };

  const handleSave = async (id) => {
    try {
      const row = users.find(user => user.id === id);
      
      if (row.uniqueOperationId !== undefined) {
        const response = await UserapiService.updateUserEntityCode({
          userId: row.id,
          objectId: row.uniqueOperationId
        });
        
        if (response.success) {
          toast.success('Object ID updated successfully!');
          setEditingRows(prev => ({ ...prev, [id]: false }));
        } else {
          toast.error(response.message || 'Failed to update entity code');
          if (response.existingEntity) {
            // toast.error(`Code already exists for ${response.existingEntity.type}: ${response.existingEntity.name}`);
          }
        }
      }
    } catch (error) {
      console.error('Error saving user:', error);
      toast.error('Error updating entity code');
    }
  };

  const columns = [
    {
      field: 'slNo',
      headerName: 'Sl No',
      width: 100,
      editable: false,
    },
    {
      field: 'firstName',
      headerName: 'First Name',
      width: 200,
      editable: false,
    },
    {
      field: 'lastName',
      headerName: 'Last Name',
      width: 200,
      editable: false,
    },
    {
      field: 'email',
      headerName: 'Email ID',
      width: 300,
      editable: false,
    },
    {
      field: 'uniqueOperationId',
      headerName: 'Object ID',
      width: 300,
      editable: false,
      renderCell: (params) => {
        const isEditing = !!editingRows[params.id];

        if (isEditing) {
          return (
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              px: 1
            }}>
              <input
                type="text"
                value={params.value || ''}
                autoFocus
                onChange={(e) => {
                  const newValue = e.target.value;
                  setUsers(prev =>
                    prev.map(user =>
                      user.id === params.id ? { ...user, uniqueOperationId: newValue } : user
                    )
                  );
                }}
                style={{
                  border: 'none',
                  background: 'transparent',
                  outline: 'none',
                  width: '100%',
                  fontSize: '14px'
                }}
              />
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  handleSave(params.id);
                }}
                color="primary"
                size="small"
                sx={{ ml: 1 }}
              >
                <Save fontSize="small" />
              </IconButton>
            </Box>
          );
        }

        return (
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            '&:hover .edit-button': {
              opacity: 1
            }
          }}>
            <span>{params.value}</span>
            <IconButton
              className="edit-button"
              onClick={(e) => {
                e.stopPropagation();
                handleEdit(params.id);
              }}
              color="primary"
              size="small"
              sx={{
                opacity: 0,
                transition: 'opacity 0.2s',
                ml: 1
              }}
            >
              <Edit fontSize="small" />
            </IconButton>
          </Box>
        );
      },
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ height: 400, margin: 5, width: '90%' }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1, width: '90%' }}>
          <TextField
            placeholder=""
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
              setPaginationModel(prev => ({ ...prev, page: 0 }));
            }}
            InputProps={{
              startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
            sx={{ width: '300px' }}
            size="small"
          />
        </Box>

        <DataGrid
          rows={users}
          columns={columns}
          loading={loading}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          rowCount={totalCount}
          paginationMode="server"
          pageSizeOptions={[10, 25, 50]}
          disableRowSelectionOnClick

          sx={{
            '& .MuiDataGrid-cell--editable': {
              backgroundColor: '#e3f2fd',
              '&:hover': {
                backgroundColor: '#bbdefb',
              },
            },
            '& .MuiDataGrid-cell:hover .edit-button': {
              opacity: 1,
            },
          }}
        />
      </Paper>
    </Box>
  );
}

export default UserPage;
